import clsx from "clsx";
import { useEffect, useState } from "react";
import { Button, Row, Table } from "react-bootstrap";
import {
  BriefcaseFill,
  ClipboardPlus,
  PencilFill,
} from "react-bootstrap-icons";
import { useHistory, useLocation } from "react-router-dom";
import {
  type Jurisdiction,
  type Objection,
  ObjectionType,
  type ObjectionVariant,
  Role,
} from "@/briefpoint-client";
import {
  DropDownFilterList,
  type DropDownItem,
} from "@/components/DropDownFilterList";
import SearchInput from "@/components/SearchInput/SearchInput";
import { TagsList } from "@/components/TagsList";
import { useTagApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { CaliforniaId } from "@/lib/constants";
import {
  getCases,
  getCaseTypeDisplay,
  getCaseTypesInUse,
  getSortedTags,
  getTagsDisplay,
  type hasCaseTypeId,
} from "@/lib/utils";
import styles from "./ObjectionTable.module.scss";

interface Props {
  objections: Objection[];
  isResponses: boolean;
}

export default function ObjectionTable({ objections, isResponses }: Props) {
  const [search, setSearch] = useState("");
  const { user, defaultJurisdiction } = useAuth();
  const [selectedObjection, setSelectedObjection] = useState<Objection>();
  const [allVariants, setAllVariants] = useState<ObjectionVariant[]>();
  const [allObjectionsSelected, setAllObjectionsSelected] = useState<boolean>(
    !isResponses,
  );
  const [availableTags, setAvailableTags] = useState<
    Map<number, string> | undefined
  >();
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [caseFilter, setCaseFilter] = useState<string[]>([]);
  const [documentFilter, setDocumentFilter] = useState<string[]>([]);
  const history = useHistory();
  const location = useLocation();
  const tagApi = useTagApi();
  const selection = new URLSearchParams(location.search).get("selection");

  const subTypes = [
    { id: "1", name: "Special Interrogatories" },
    { id: "2", name: "Requests for Production" },
    { id: "3", name: "Requests for Admission" },
    { id: "5", name: "Form Interrogatories" },
  ];

  function getMiscDisplay(active: string[], msc: DropDownItem[]) {
    let ret = msc.find((x) => x.id === active[0])?.name;

    if (active.length > 1) {
      ret += ` (+${active.length - 1})`;
    }
    return ret;
  }

  useEffect(() => {
    async function loadTags() {
      const tagsList = new Map<number, string>();
      const loadedTags = await tagApi.tagFindTags({});

      for (const t of loadedTags) {
        tagsList.set(t.id, t.name);
      }
      setAvailableTags(tagsList);
    }
    loadTags();
  }, [tagApi]);

  useEffect(() => {
    if (objections?.length) {
      if (selectedObjection) {
        setSelectedObjection(
          objections.find((o) => o.id === selectedObjection.id),
        );
      } else {
        setSelectedObjection(
          objections.filter(
            (objection) =>
              (!isResponses &&
                objection.objectionType !== ObjectionType.Response &&
                objection.objectionType !== ObjectionType.NoResponse) ||
              (isResponses &&
                (objection.objectionType === ObjectionType.Response ||
                  objection.objectionType === ObjectionType.NoResponse)),
          )[0],
        );
      }
      const allTheVariants: ObjectionVariant[] = [];
      for (const objection of objections) {
        for (const variant of objection.variants) {
          allTheVariants.push(variant);
        }
      }
      setAllVariants(allTheVariants);
    }
  }, [isResponses, objections, selectedObjection]);

  useEffect(() => {
    if (objections && selection) {
      for (const objection of objections.filter(
        (objection) =>
          (!isResponses &&
            objection.objectionType !== ObjectionType.Response &&
            objection.objectionType !== ObjectionType.NoResponse) ||
          (isResponses &&
            (objection.objectionType === ObjectionType.Response ||
              objection.objectionType === ObjectionType.NoResponse)),
      )) {
        if (objection.id === Number.parseInt(selection, 10)) {
          setAllObjectionsSelected(false);
          setSelectedObjection(objection);
        }
      }
    }
  }, [isResponses, objections, selection]);

  function getSortedCases(
    allVariants: hasCaseTypeId[],
    ca_superior_court: Jurisdiction,
    caseFilter?: unknown[],
  ) {
    const sortedCases =
      getCaseTypesInUse(allVariants ?? [], ca_superior_court)
        .sort((a, b) => {
          const aSelected = caseFilter?.includes(a.id);
          const bSelected = caseFilter?.includes(b.id);

          if (aSelected && !bSelected) {
            return -1;
          }

          if (bSelected && !aSelected) {
            return 1;
          }

          return a.name.localeCompare(b.name);
        })
        .map((cc) => ({ name: cc.name ?? "", id: cc.id })) ?? [];

    return sortedCases;
  }

  function setSelectionUrl(newUrl: number | null) {
    if (newUrl === null) {
      history.push({
        pathname: "/library",
        search: `?tab=${isResponses ? "responses" : "objections"}`,
      });
    } else {
      history.push({
        pathname: "/library",
        search: `?tab=${isResponses ? "responses" : "objections"}&selection=${newUrl}`,
      });
    }
  }

  function getVariantsCaseType(variant: ObjectionVariant) {
    const cases = getCases(defaultJurisdiction);

    if (variant.caseTypeId) {
      return cases.find((c) => c.id === variant.caseTypeId)?.name;
    }
    return "All";
  }

  function getVariantsObjections(variant: ObjectionVariant) {
    if (objections) {
      for (const objection of objections) {
        if (variant.objectionId === objection.id) {
          return objection.name;
        }
      }
    }
    return "";
  }

  function getVariantsDocumentType(variant: ObjectionVariant) {
    let subTypes: { [key: number]: string } = {
      1: "Interrogatories",
      2: "Requests for Production",
      3: "Requests for Admission",
    };

    if (CaliforniaId === defaultJurisdiction?.id) {
      subTypes = {
        1: "Special Interrogatories",
        2: "Requests for Production",
        3: "Requests for Admission",
        5: "Form Interrogatories",
      };
    }

    if (variant?.documentSubType) {
      const subType = subTypes[variant.documentSubType];
      return subType ?? "All";
    }
    return "All";
  }

  function addVariant() {
    if (!allObjectionsSelected && selectedObjection) {
      history.push({
        pathname: "/library/addedit",
        search: `?type=${selectedObjection.id}`,
      });
    } else {
      history.push({
        pathname: "/library/addedit",
      });
    }
  }

  function editVariant(variant: ObjectionVariant, copy = false) {
    history.push({
      pathname: "/library/addedit",
      search: `${copy ? "?copy=true" : ""}`,
      state: variant,
    });
  }

  function handleClearEvent() {
    setTagFilter([]);
    setCaseFilter([]);
    setDocumentFilter([]);
  }

  return (
    <div
      className={`${styles.stepContainer} ${styles.objectionTableContainer}`}
      style={{ gap: 0 }}
    >
      <div className={styles.librarySidebar}>
        {!isResponses && (
          // biome-ignore lint/a11y/useKeyWithClickEvents: TODO: replace with button
          <div
            key={"all"}
            onClick={() => {
              setAllObjectionsSelected(true);
              setSelectedObjection(undefined);
              setSelectionUrl(null);
            }}
            className={clsx(styles.libraryObjection, {
              [styles.libraryObjectionSelected]: allObjectionsSelected,
            })}
          >
            All Objections
          </div>
        )}
        {objections
          ?.filter(
            (objection) =>
              (!isResponses &&
                objection.objectionType !== ObjectionType.Response &&
                objection.objectionType !== ObjectionType.NoResponse) ||
              (isResponses &&
                (objection.objectionType === ObjectionType.Response ||
                  objection.objectionType === ObjectionType.NoResponse)),
          )
          .map((o) => (
            // biome-ignore lint/a11y/useKeyWithClickEvents: TODO: replace with button
            <div
              key={o.id}
              onClick={() => {
                setAllObjectionsSelected(false);
                setSelectedObjection(o);
                setSelectionUrl(o.id);
              }}
              className={clsx(styles.libraryObjection, {
                [styles.libraryObjectionSelected]:
                  selectedObjection === o && !allObjectionsSelected,
              })}
            >
              {o.name}
            </div>
          ))}
      </div>
      <div className={styles.libraryContent}>
        <div className={styles.libraryControlsRow}>
          <div className={styles.librarySearchCol}>
            <SearchInput
              value={search}
              placeholder="Search title"
              onChange={(value) => setSearch(value.toLowerCase())}
            />
          </div>
          <div className={styles.libraryFiltersRow}>
            <div className={styles.libraryFiltersCol}>
              <div className={styles.filtersWrapper}>
                <b className={styles.filterLabelHeader}>Filter By:</b>
                <div className={styles.filtersGroup}>
                  <div className={styles.filterDropdownWrapper}>
                    {!!documentFilter.length && (
                      <label
                        className={styles.filterLabel}
                        htmlFor="document-filter"
                      >
                        Document Type
                      </label>
                    )}
                    <DropDownFilterList
                      key="document-filter"
                      text={
                        !documentFilter.length
                          ? "Document Type"
                          : getMiscDisplay(documentFilter, subTypes)
                      }
                      itemsList={subTypes}
                      activeFilter={documentFilter}
                      updateFilter={setDocumentFilter}
                    />
                  </div>
                  <div className={styles.filterDropdownWrapper}>
                    {!!caseFilter.length && (
                      <label
                        className={styles.filterLabel}
                        htmlFor="case-filter"
                      >
                        Case Type
                      </label>
                    )}
                    <DropDownFilterList
                      key="case-filter"
                      text={
                        !caseFilter.length
                          ? "Case Type"
                          : getCaseTypeDisplay(caseFilter, defaultJurisdiction)
                      }
                      itemsList={getSortedCases(
                        allVariants ?? [],
                        defaultJurisdiction,
                        caseFilter,
                      )}
                      activeFilter={caseFilter}
                      updateFilter={setCaseFilter}
                    />
                  </div>
                  <div className={styles.filterDropdownWrapper}>
                    {!!tagFilter.length && (
                      <label
                        className={styles.filterLabel}
                        htmlFor="tags-filter"
                      >
                        Tags
                      </label>
                    )}
                    <DropDownFilterList
                      key="tags-filter"
                      text={
                        !tagFilter.length
                          ? "Tags"
                          : getTagsDisplay(tagFilter, availableTags)
                      }
                      itemsList={getSortedTags(
                        allVariants ?? [],
                        availableTags,
                        tagFilter,
                      )}
                      activeFilter={tagFilter}
                      updateFilter={setTagFilter}
                    />
                  </div>
                  {(!!caseFilter.length ||
                    !!tagFilter.length ||
                    !!documentFilter.length) && (
                    <Button
                      className={`${styles.libraryClearBtn} clear-btn`}
                      variant="link"
                      onClick={handleClearEvent}
                    >
                      Clear All
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.libraryButtonCol}>
              <Button
                variant="primary"
                onClick={addVariant}
                className={styles.libraryCreateButton}
              >
                Create New
              </Button>
            </div>
          </div>
        </div>
        <Row>
          <div className={styles.tableResponsiveWrapper}>
            <Table striped hover className={styles.objectionDataTable}>
              <thead>
                <tr className={styles.tableHeader}>
                  <th />
                  <th className={styles.tableHeaderCell} scope="">
                    Variant Title
                  </th>
                  <th className={styles.tableHeaderCell} scope="col">
                    Objection Type
                  </th>
                  <th scope="col" className={styles.tableHeaderCell}>
                    Document Type
                  </th>
                  <th scope="col" className={styles.tableHeaderCell}>
                    Case Type
                  </th>
                  <th scope="col" className={styles.tableHeaderCell}>
                    Tags
                  </th>
                  <th className={styles.tableHeaderCell} scope="col" />
                </tr>
              </thead>
              <tbody>
                {(allObjectionsSelected
                  ? allVariants
                  : selectedObjection?.variants
                )
                  ?.filter(
                    (variant) =>
                      variant?.name.toLowerCase().includes(search) &&
                      !variant?.isDeleted,
                  )
                  ?.filter((v) => {
                    if (documentFilter.length > 0) {
                      return !!documentFilter.find(
                        (df) =>
                          df === v.documentSubType?.toString() &&
                          v.documentSubType !== null,
                      );
                    }
                    return true;
                  })
                  ?.filter((x) => {
                    if (caseFilter.length > 0) {
                      return !!caseFilter.find(
                        (cf) => cf === x.caseTypeId && x.caseTypeId !== null,
                      );
                    }
                    return true;
                  })
                  ?.filter((y) => {
                    if (tagFilter.length > 0) {
                      return !!tagFilter.find(
                        (tf) =>
                          !!y.tagIds &&
                          !!y.tagIds.find((id) => id === Number(tf)),
                      );
                    }
                    return true;
                  })
                  .map((variant) => (
                    <tr key={variant.id} className={styles.tableRow}>
                      <td style={{ paddingLeft: 10 }}>
                        {variant.sharedWithFirmId ? (
                          <BriefcaseFill title="Firm Standard" />
                        ) : (
                          " "
                        )}
                      </td>
                      <td className={styles.tableCell}>{variant.name}</td>
                      <td className={styles.tableCell}>
                        {getVariantsObjections(variant)}
                      </td>
                      <td className={styles.tableCell}>
                        {getVariantsDocumentType(variant)}
                      </td>
                      <td className={styles.tableCell}>
                        {getVariantsCaseType(variant)}
                      </td>
                      <td className={styles.tableCell}>
                        <TagsList
                          tagIds={variant?.tagIds}
                          availableTags={availableTags}
                          questionId={variant?.id?.toString() ?? ""}
                          startTrimmingTagsAfter={3}
                        />
                      </td>
                      <td
                        className={`${styles.actionsCell} ${styles.tableCell}`}
                      >
                        {(user?.role?.includes(Role.FirmAdmin) ||
                          !variant.sharedWithFirmId) && (
                          <PencilFill
                            onClick={() => editVariant(variant)}
                            className={styles.tableEditItem}
                            title="Edit"
                          />
                        )}
                        <ClipboardPlus
                          onClick={() => editVariant(variant, true)}
                          className={styles.tableEditItem}
                          title="Copy"
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        </Row>
      </div>
    </div>
  );
}
